import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { Typography } from '@/components/ui/typography';
import { formatShortDate } from '@/utils/date-util';
import { getRangeTimeZoneLabel, localDayAt } from '@/utils/time-zone';

import { DateRangePanel } from '../date-range-panel';
import { TimeZoneChip, TimeZoneChipZone } from '../time-zone-chip';

interface HiringGoalDateRangeProps {
  startDate: string;
  endDate?: string;
  daysElapsed: number;
  addEndDateUrl: string;
  /** The opening's IANA zone; when set, the panel shows it in a chip below the dates. */
  timeZone?: string;
}

export function HiringGoalDateRange({
  startDate,
  endDate,
  daysElapsed,
  addEndDateUrl,
  timeZone,
}: HiringGoalDateRangeProps) {
  // The label names the zone for the goal's days at the opening, not in the browser.
  const timeZoneLabel = useMemo(() => {
    if (!timeZone) return null;
    const startDay = localDayAt(new Date(startDate), timeZone);
    if (!startDay) return null;
    // An open-ended goal has no last day, so no one abbreviation holds for all of it.
    const endDay = endDate ? localDayAt(new Date(endDate), timeZone) : null;
    return endDay
      ? getRangeTimeZoneLabel(startDay, endDay, timeZone)
      : timeZone;
  }, [startDate, endDate, timeZone]);

  return (
    <DateRangePanel
      timeZoneChip={
        timeZone &&
        timeZoneLabel && (
          <TimeZoneChip timeZone={timeZone}>
            <FormattedMessage
              defaultMessage="All times in <zone>{timeZone}</zone>"
              description="Chip below the hiring goal's dates. {timeZone} is an IANA zone with its abbreviation, such as America/Chicago (CDT)"
              values={{
                timeZone: timeZoneLabel,
                zone: chunks => <TimeZoneChipZone>{chunks}</TimeZoneChipZone>,
              }}
            />
          </TimeZoneChip>
        )
      }
      subtitle={
        <FormattedMessage
          defaultMessage="Elapsed: {count, plural, one {# day} other {# days}}"
          description="Elapsed days in the hiring goal; {count} is the number of days"
          values={{ count: daysElapsed }}
        />
      }
    >
      <Typography
        variant="body2"
        className="text-fx-neutral-900 font-medium whitespace-nowrap"
      >
        {formatShortDate(startDate)}
        {' – '}
        {endDate ? (
          formatShortDate(endDate)
        ) : (
          <Link
            to={addEndDateUrl}
            target="_blank"
            className="text-fx-primary underline underline-offset-4 hover:underline"
          >
            <FormattedMessage
              defaultMessage="+ Add end date"
              description="Link to add an end date to the hiring goal"
            />
          </Link>
        )}
      </Typography>
    </DateRangePanel>
  );
}
